/** 装箱单 Excel 导出数据构建：相同内容的箱子分组生成明细行与合并区域 */
import type { ExportColumn } from "@/components/ExportExcelButton";
import type { BoxItem, PackBox, PackOrder } from "@/types/packing";
import { wmslotV2Parse } from "@/utils/codeParse";
import i18next from "i18next";
import * as XLSX from "xlsx-js-style";

/** 装箱分组：内容一致的一批箱子；items 以首箱为代表，价格取组内最新批次 */
interface PackingGroup {
  boxes: PackBox[];
  items: BoxItem[];
}

/** 箱级别字段的列索引（0 基）：2 箱号、8 毛重/箱、9 箱数、12 总毛重、13~15 长宽高、16 体积 */
const BOX_LEVEL_COLUMN_INDEXES = [2, 8, 9, 12, 13, 14, 15, 16];
/** 22 列的字段 key（与导出行的字段名一一对应） */
const COLUMN_KEYS = [
  "totolinkModel",
  "zioncomModel",
  "boxNumbers",
  "code",
  "name",
  "model",
  "quantityPerBox",
  "netWeightPerBox",
  "grossWeightPerBox",
  "boxCount",
  "quantity",
  "netWeight",
  "grossWeight",
  "length",
  "width",
  "height",
  "volume",
  "productModel",
  "brand",
  "originPlace",
  "price",
  "currency",
] as const;

const thinBorder: NonNullable<XLSX.CellStyle["border"]> = {
  top: { style: "thin", color: { rgb: "000000" } },
  bottom: { style: "thin", color: { rgb: "000000" } },
  left: { style: "thin", color: { rgb: "000000" } },
  right: { style: "thin", color: { rgb: "000000" } },
};

/** 表头居中且不自动换行；不设置填充颜色 */
const headerStyle: XLSX.CellStyle = {
  alignment: { horizontal: "center", vertical: "center" },
  border: thinBorder,
};

/** 数据区默认水平、垂直居中；不自动换行，避免 Excel/WPS 自动撑高行高 */
const bodyStyle: XLSX.CellStyle = {
  alignment: { horizontal: "center", vertical: "center" },
  border: thinBorder,
};

const totalStyle: XLSX.CellStyle = {
  ...bodyStyle,
  font: { bold: true },
};

/** Description 数据保持左对齐，便于阅读较长的型号描述 */
const descriptionStyle: XLSX.CellStyle = {
  ...bodyStyle,
  alignment: { horizontal: "left", vertical: "center" },
};

const createCell = (value: string | number, style: XLSX.CellStyle): XLSX.CellObject => ({
  t: typeof value === "number" ? "n" : "s",
  v: value,
  s: style,
});

/** 导出数据行：字段名为 key，值为单元格内容（箱级别字段在非首行为空字符串） */
type ExcelCellValue = string | number | XLSX.CellObject | undefined;
export type PackingExcelRow = Record<string, ExcelCellValue>;

/** 导出列定义（key 与 label 同名，实际表头文案由 packingExcelHeaders 提供） */
export const packingExcelColumns: ExportColumn[] = COLUMN_KEYS.map((key) => ({ key, label: key }));

/** 装箱单各列宽度，保证固定表头尽量保持单行显示 */
export const packingExcelColumnWidths = [16, 17, 14, 12, 14, 32, 12, 12, 12, 10, 12, 12, 12, 10, 10, 10, 12, 20, 16, 12, 14, 12];

// 获取批次时间戳，用于比较不同明细的批次先后顺序
const getBatchTimestamp = (item: BoxItem) => {
  const productDate = wmslotV2Parse(item.lot ?? "").lotData.productDate;
  const timestamp = Number(productDate);
  return productDate.trim() && Number.isFinite(timestamp) ? timestamp : -Infinity;
};

/** 仅对满足合并条件的明细比较批次；target 是导出副本，不修改原始 item。 */
const retainLatestPrice = (target: BoxItem, item: BoxItem) => {
  if (getBatchTimestamp(item) > getBatchTimestamp(target)) {
    target.lot = item.lot;
    target.declaredUnitPrice = item.declaredUnitPrice;
    target.declaredCurrency = item.declaredCurrency;
  }
};

/** 同箱同物料同品牌同源产地同币种的明细合并数量和净重；保留首条位置，不修改原始明细。 */
const mergeBoxItems = (items: BoxItem[]): BoxItem[] => {
  const getItemKey = (item: BoxItem) => JSON.stringify({
    code: item.product?.code ?? item.productId,
    brand: item.brand ?? "",
    originPlace: item.originPlace ?? "",
    currency: item.declaredCurrency ?? "",
  });
  const mergedItems: BoxItem[] = [];
  const itemMap = new Map<string, BoxItem>();

  items.forEach((item) => {
    const key = getItemKey(item);
    const existingItem = itemMap.get(key);
    if (existingItem) {
      existingItem.count = (existingItem.count ?? 0) + (item.count ?? 0);
      existingItem.netWeight = (existingItem.netWeight ?? 0) + (item.netWeight ?? 0);
      retainLatestPrice(existingItem, item);
    } else {
      const mergedItem = { ...item };
      itemMap.set(key, mergedItem);
      mergedItems.push(mergedItem);
    }
  });

  return mergedItems;
};

/** 提取参与分组比对的物料关键字段（编码/品牌/源产地/币种/数量），其余字段不参与 */
const getComparableItem = (item: BoxItem) => ({
  code: item.product?.code ?? "",
  brand: item.brand ?? "",
  originPlace: item.originPlace ?? "",
  count: item.count ?? 0,
  currency: item.declaredCurrency ?? "",
});

/** 按"内容一致性"分组；分组键 = 毛重 + 后端已排序的物料关键项。空箱跳过 */
const getPackingGroups = (boxes: PackBox[]): PackingGroup[] => {
  const groupMap = new Map<string, PackingGroup>();

  boxes.forEach((box) => {
    const items = mergeBoxItems(box.items ?? []);
    if (items.length === 0) return;

    const comparableItems = items.map(getComparableItem);
     const groupKey = JSON.stringify({
      items: comparableItems,
      grossWeight: box.grossWeightG,
    });
    const existingGroup = groupMap.get(groupKey);

    if (existingGroup) {
      existingGroup.boxes.push(box);
      items.forEach((item, index) => retainLatestPrice(existingGroup.items[index], item));
      return;
    }

    groupMap.set(groupKey, { boxes: [box], items });
  });

  return Array.from(groupMap.values());
};

/** 登记一个合并区域（行列均为 0 基，含 2 行表头的整表坐标） */
const addMerge = (
  merges: XLSX.Range[],
  startRow: number,
  startColumn: number,
  endRow: number,
  endColumn: number,
) => {
  merges.push({
    s: { r: startRow, c: startColumn },
    e: { r: endRow, c: endColumn },
  });
};

/** 固定表头，第 0 行字段名，第 1 行计量单位 */
export const packingExcelHeaders: ExcelCellValue[][] = [
  [
    "TOTOLINK MODEL", // 0  预留列，数据为空
    "ZIONCOM Model",  // 1  预留列，数据为空
    "箱号",           // 2  箱号（纵向合并两行）
    "ERP",            // 3  存货编码
    "ORDER NO.",      // 4  存货名称
    "Description",    // 5  存货型号
    "q'ty/ctn",       // 6  数量/箱
    "n.w/ctn",        // 7  净重/箱
    "g.w/ctn",        // 8  毛重/箱
    "ctns",           // 9  箱数
    "q'ty",           // 10 数量
    "N.W",            // 11 净重
    "G.W",            // 12 毛重
    "measurements",   // 13 尺寸（横跨 13~15 三列）
    "",               // 14 （尺寸 W，占位）
    "",               // 15 （尺寸 H，占位）
    "",               // 16 体积（按参考表保留空表头）
    i18next.t("warehouse.packingModel"), // 17 型号
    i18next.t("warehouse.brand"),       // 18 品牌
    i18next.t("warehouse.packingOrigin"), // 19 产地
    i18next.t("requirement.price"), // 20 价格
    i18next.t("warehouse.declaredCurrency"), // 21 币种
  ],
  ["", "", "", "", "", "", "(pcs)", "(kgs)", "(kgs)", "", "(pcs)", "(kgs)", "(kgs)", "L(cm)", "W(cm)", "H(cm)", "", "", "", "", "", ""],
].map((row) => row.map((value) => createCell(value, headerStyle)));

/** 导出只依赖单号（文件名）和箱子数据，合并导出时无需构造完整的 PackOrder */
export type PackingExportData = Pick<PackOrder, "number" | "boxes">;

/**
 * 构建装箱单导出数据（不含表头）。
 * @returns rows 明细及末尾合计行；merges 合并区域（整表 0 基坐标）
 */
export const buildPackingExcelData = (order: PackingExportData) => {
  const groups = getPackingGroups(order.boxes ?? []);
  const rows: PackingExcelRow[] = [];
  const merges: XLSX.Range[] = [];

  // 表头合并：前六列、箱数、体积及末五列纵向合并两行；"measurements" 横向合并 L/W/H 三列
  [0, 1, 2, 3, 4, 5, 9, 16, 17, 18, 19, 20, 21].forEach((column) => addMerge(merges, 0, column, 1, column));
  addMerge(merges, 0, 13, 0, 15);

  groups.forEach((group) => {
    const firstBox = group.boxes[0];             // 首箱作为组代表
    const boxCount = group.boxes.length;
    const groupStartRowIndex = rows.length + 2;  // 本组首行的整表行号（+2 跳过两行表头）
    const grossWeight = firstBox.grossWeightG ?? 0;
    const length = firstBox.lengthMm ?? 0;
    const width = firstBox.widthMm ?? 0;
    const height = firstBox.heightMm ?? 0;
    // 组体积 = 首箱后端返回体积 × 箱数。
    const volume = (firstBox.volumeCbm ?? 0) * boxCount;
    const boxNumbers = group.boxes.map((box) => box.boxNumber).filter(Boolean).join(", ");

    // 组内每种物料一行；箱级别的值仅写在首行（isFirstItem），其余行留空待合并
    group.items.forEach((item, itemIndex) => {
      const isFirstItem = itemIndex === 0;
      const quantity = item.count ?? 0;
      const netWeight = item.netWeight ?? 0;
      const originPlace = item.originPlace ?? "无";

      rows.push({
        totolinkModel: createCell("", bodyStyle),
        zioncomModel: createCell("", bodyStyle),
        boxNumbers: createCell(isFirstItem ? boxNumbers : "", bodyStyle),                         // 箱号列表（箱级别）
        code: createCell(String(item.product?.code ?? ""), bodyStyle),
        name: createCell(item.product?.name ?? "", bodyStyle),
        model: createCell(item.product?.model ?? "", descriptionStyle),
        quantityPerBox: createCell(quantity, bodyStyle),                                      // 数量/箱
        netWeightPerBox: createCell(netWeight, bodyStyle),                                     // 净重/箱
        grossWeightPerBox: createCell(isFirstItem ? grossWeight : "", bodyStyle),              // 毛重/箱（箱级别）
        boxCount: createCell(isFirstItem ? boxCount : "", bodyStyle),                         // 箱数（箱级别）
        quantity: createCell(quantity * boxCount, bodyStyle),                                 // 总数量 = 数量/箱 × 箱数
        netWeight: createCell(netWeight * boxCount, bodyStyle),                                // 总净重 = 净重/箱 × 箱数
        grossWeight: createCell(isFirstItem ? grossWeight * boxCount : "", bodyStyle),         // 总毛重（箱级别）
        length: createCell(isFirstItem ? length : "", bodyStyle),                              // 长 L(cm)（箱级别）
        width: createCell(isFirstItem ? width : "", bodyStyle),                                // 宽 W(cm)（箱级别）
        height: createCell(isFirstItem ? height : "", bodyStyle),                              // 高 H(cm)（箱级别）
        volume: createCell(isFirstItem ? volume : "", bodyStyle),                              // 体积 m³（箱级别）
        productModel: createCell(item.brandType || "无", descriptionStyle),
        brand: createCell(item.brand || "无", bodyStyle),
        originPlace: createCell(originPlace, bodyStyle),
        price: createCell(item.declaredUnitPrice ?? "", bodyStyle),
        currency: createCell(item.declaredCurrency ?? "", bodyStyle),
      });
    });

    // 组内有多个物料行时，箱级别列纵向合并（+1 为本组末行的整表行号）
    const groupEndRowIndex = rows.length + 1;
    if (groupEndRowIndex > groupStartRowIndex) {
      BOX_LEVEL_COLUMN_INDEXES.forEach((column) => (
        addMerge(merges, groupStartRowIndex, column, groupEndRowIndex, column)
      ));
    }
  });

  const totalRow: PackingExcelRow = {};
  COLUMN_KEYS.forEach((key) => {
    totalRow[key] = createCell("", totalStyle);
  });
  totalRow.grossWeightPerBox = createCell("总计", totalStyle);

  const firstDataRow = packingExcelHeaders.length + 1;
  const lastDataRow = packingExcelHeaders.length + rows.length;
  // 仅写入公式，由 Excel/WPS 计算合计；SUM 会忽略合并列的空白占位。
  (["boxCount", "quantity", "netWeight", "grossWeight", "volume"] as const).forEach((key) => {
    const column = XLSX.utils.encode_col(COLUMN_KEYS.indexOf(key));
    totalRow[key] = rows.length === 0 ? createCell(0, totalStyle) : {
      t: "n",
      f: `SUM(${column}${firstDataRow}:${column}${lastDataRow})`,
      s: totalStyle,
    };
  });
  rows.push(totalRow);

  return { rows, merges };
};
